const VIBE_COLLECTION_HANDLES = {
  her_vibe: 'her-vibe',
  boys_vibe: 'his-vibe',
  mini_vibe: 'mini-vibe',
  hype_vibe: 'hype-vibe',
  univibe: 'univibe',
}

const COLLECTION_DISPLAY_NAMES = {
  'her-vibe': 'Her Vibe',
  'his-vibe': 'His Vibe',
  'mini-vibe': 'Mini Vibe',
  'hype-vibe': 'Hype Vibe',
  'caps-hats': 'Caps & Hats',
  'combo-deals': 'Combo Deals',
  univibe: 'Uni Vibe',
}

const COLLECTION_SELECTION_RULES = {
  hervibe: {
    allowedTokens: ['her'],
  },
  hisvibe: {
    allowedTokens: ['his'],
  },
  minivibe: {
    allowedTokens: ['mini'],
  },
  hypevibe: {
    allowedTokens: ['hype'],
  },
  univibe: {
    allowedTokens: ['uni'],
  },
  premiumvibe: {
    allowedTokens: ['premiumvibe'],
    alwaysMatch: true,
  },
}

const COLLECTION_SELECTION_ALIASES = {
  all: 'all',
  allvibes: 'all',
  boys: 'hisvibe',
  boysvibe: 'hisvibe',
  her: 'hervibe',
  hervibe: 'hervibe',
  his: 'hisvibe',
  hisvibe: 'hisvibe',
  mini: 'minivibe',
  minivibe: 'minivibe',
  hype: 'hypevibe',
  hypevibe: 'hypevibe',
  uni: 'univibe',
  univibe: 'univibe',
  unisex: 'univibe',
  premium: 'premiumvibe',
  premiumvibe: 'premiumvibe',
  limiteddrops: 'premiumvibe',
}

const SEARCH_SYNONYM_MAP = {
  cup: ['mug', 'tumbler'],
  mug: ['cup', 'tumbler'],
  tumbler: ['cup', 'mug'],
  tshirt: ['t-shirt', 'tee', 'tees', 't shirt', 't shirts'],
  tees: ['tee', 'tshirt', 't-shirt', 't shirt', 't shirts'],
  tee: ['tshirt', 't-shirt', 'tees', 't shirt', 't shirts'],
  oversized: [
    'oversize',
    'baggy',
    'relaxed',
    'boxy',
    'drop shoulder',
    'drop-shoulder',
    'tee',
    'tees',
    'tshirt',
    'hoodie',
    'sweatshirt',
  ],
  hoodie: ['sweatshirt', 'jacket', 'pullover'],
  sweatshirt: ['hoodie', 'jacket', 'pullover'],
  jacket: ['hoodie', 'sweatshirt', 'pullover'],
  pullover: ['hoodie', 'sweatshirt', 'jacket'],
}

const SEARCH_BROAD_KEYS = new Set(['oversized'])
const SEARCH_FIELD_PRIORITIES = {
  title: 0,
  category: 0,
  productType: 1,
  collections: 2,
  tags: 3,
}
const SEARCH_STYLE_MODIFIERS = ['oversized', 'oversize', 'baggy', 'relaxed', 'boxy', 'dropshoulder']

function normalizeText(value) {
  return String(value ?? '').trim()
}

export function cleanTag(tag) {
  return String(tag ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

export function normalizeCollectionHandle(value) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function matchesCollectionHandle(product, collectionHandle) {
  return matchesCatalogSelection(product, collectionHandle)
}

function normalizeTagToken(value) {
  return cleanTag(value)
}

function normalizeSelectionKey(value) {
  return normalizeTagToken(value)
}

function normalizeSearchKey(value) {
  return normalizeSearchText(value).replace(/\s+/g, '')
}

function normalizeSearchText(value) {
  return normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function collectTagValues(source) {
  if (Array.isArray(source)) {
    return source.flatMap((value) => collectTagValues(value))
  }

  if (source && typeof source === 'object') {
    const directValues = [
      source?.name,
      source?.title,
      source?.label,
      source?.value,
      source?.tag,
      source?.handle,
      source?.slug,
    ]
      .map((value) => normalizeText(value))
      .filter(Boolean)

    if (directValues.length) {
      return directValues
    }

    return Object.values(source).flatMap((value) => collectTagValues(value))
  }

  const normalizedValue = normalizeText(source)

  if (!normalizedValue) {
    return []
  }

  return normalizedValue
    .split(/,|\n|\u2022|\|/)
    .map((value) => normalizeText(value))
    .filter(Boolean)
}

function getProductTagValues(product) {
  const tagSources = [
    product?.tags,
    product?.product_tags,
    product?.node?.tags,
    product?.node?.product_tags,
    product?.vibe_category,
    product?.category,
    product?.product_category,
  ]

  return Array.from(
    new Set(tagSources.flatMap((source) => collectTagValues(source)).filter(Boolean)),
  )
}

function collectSearchValues(source) {
  if (Array.isArray(source)) {
    return source.flatMap((value) => collectSearchValues(value))
  }

  if (source && typeof source === 'object') {
    const directValues = [
      source?.name,
      source?.title,
      source?.label,
      source?.value,
      source?.tag,
      source?.handle,
      source?.slug,
      source?.productType,
      source?.product_type,
    ]
      .map((value) => normalizeText(value))
      .filter(Boolean)

    if (directValues.length) {
      return directValues
    }

    return Object.values(source).flatMap((value) => collectSearchValues(value))
  }

  const normalizedValue = normalizeText(source)

  if (!normalizedValue) {
    return []
  }

  return normalizedValue
    .split(/,|\n|\u2022|\||;/)
    .map((value) => normalizeText(value))
    .filter(Boolean)
}

function getProductCollectionValues(product) {
  const collectionSources = [
    product?.collections,
    product?.collectionTitles,
    product?.collection_titles,
    product?.collectionTitle,
    product?.collectionHandle,
    product?.collection_handle,
    product?.collectionHandles,
  ]

  return Array.from(
    new Set(
      collectionSources
        .flatMap((source) => collectSearchValues(source))
        .filter(Boolean),
    ),
  )
}

function getProductSearchValues(product) {
  const title = normalizeSearchKey(product?.title ?? product?.name ?? '')
  const category = normalizeSearchKey(
    product?.category ?? product?.product_category ?? product?.vibe_category ?? '',
  )
  const productType = normalizeSearchKey(product?.productType ?? product?.product_type ?? '')
  const tags = getProductTagValues(product)
    .map((tag) => normalizeSearchKey(tag))
    .filter(Boolean)
  const collections = getProductCollectionValues(product)
    .map((collection) => normalizeSearchKey(collection))
    .filter(Boolean)

  return { title, category, productType, tags, collections }
}

function getSearchVariants(searchQuery) {
  const normalizedSearchText = normalizeSearchText(searchQuery)
  const normalizedSearchKey = normalizeSearchKey(searchQuery)

  if (!normalizedSearchKey) {
    return []
  }

  const variants = []
  const seenVariants = new Set()

  function addVariant(value, kind = 'query') {
    const text = normalizeSearchText(value)
    const key = normalizeSearchKey(value)

    if (!text && !key) {
      return
    }

    const resolvedText = text || key
    const resolvedKey = key || normalizeSearchKey(text)
    const dedupeKey = `${kind}:${resolvedKey}:${resolvedText}`

    if (seenVariants.has(dedupeKey)) {
      return
    }

    seenVariants.add(dedupeKey)
    variants.push({
      kind,
      key: resolvedKey,
      text: resolvedText,
    })
  }

  addVariant(normalizedSearchText)
  addVariant(normalizedSearchKey)

  const synonyms = SEARCH_SYNONYM_MAP[normalizedSearchKey] ?? []
  const synonymKind = SEARCH_BROAD_KEYS.has(normalizedSearchKey)
    ? 'broad'
    : 'synonym'

  synonyms.forEach((synonym) => {
    addVariant(synonym, synonymKind)
  })

  return variants
}

function getProductTagTokens(product) {
  const strictTagSources = [
    product?.tags,
    product?.product_tags,
    product?.node?.tags,
    product?.node?.product_tags,
  ]

  return strictTagSources
    .flatMap((source) => collectTagValues(source))
    .map((tag) => normalizeTagToken(tag))
    .filter(Boolean)
}

function getSearchTermGroups(searchQuery) {
  const normalizedSearchText = normalizeSearchText(searchQuery)
  const normalizedSearchKey = normalizeSearchKey(searchQuery)

  if (!normalizedSearchKey) {
    return []
  }

  const searchTerms = []

  if (normalizedSearchText) {
    searchTerms.push(normalizedSearchText)
  }

  if (normalizedSearchKey && normalizedSearchKey !== normalizeSearchKey(normalizedSearchText)) {
    searchTerms.push(normalizedSearchKey)
  }

  normalizedSearchText
    .split(' ')
    .filter(Boolean)
    .forEach((term) => {
      searchTerms.push(term)
    })

  return Array.from(new Set(searchTerms.filter(Boolean)))
}

function getProductSearchStylePenalty(product, searchQuery = '') {
  const searchKey = normalizeSearchKey(searchQuery)

  if (!searchKey) {
    return 0
  }

  if (SEARCH_STYLE_MODIFIERS.some((modifier) => searchKey.includes(modifier))) {
    return 0
  }

  const { title, category, productType, tags, collections } = getProductSearchValues(product)
  const searchText = normalizeSearchKey([
    title,
    category,
    productType,
    ...tags,
    ...collections,
  ].join(' '))

  return SEARCH_STYLE_MODIFIERS.some((modifier) => searchText.includes(modifier)) ? 1 : 0
}

function scoreSearchVariantAgainstField(fieldValue, variant, fieldPriority) {
  const normalizedFieldText = normalizeSearchText(fieldValue)
  const normalizedFieldKey = normalizeSearchKey(fieldValue)

  if (!normalizedFieldText && !normalizedFieldKey) {
    return null
  }

  const variantText = normalizeSearchText(variant?.text ?? '')
  const variantKey = normalizeSearchKey(variant?.key ?? variant?.text ?? '')

  if (!variantText && !variantKey) {
    return null
  }

  const sourcePenalty =
    variant?.kind === 'broad' ? 120 : variant?.kind === 'synonym' ? 60 : 0

  if (normalizedFieldText === variantText || normalizedFieldKey === variantKey) {
    return fieldPriority * 100 + sourcePenalty
  }

  if (
    normalizedFieldText.startsWith(variantText) ||
    normalizedFieldKey.startsWith(variantKey)
  ) {
    return fieldPriority * 100 + 20 + sourcePenalty
  }

  if (
    normalizedFieldText.includes(variantText) ||
    normalizedFieldKey.includes(variantKey)
  ) {
    return fieldPriority * 100 + 40 + sourcePenalty
  }

  return null
}

function scoreProductSearchTerm(product, searchTerm) {
  const variants = getSearchVariants(searchTerm)

  if (!variants.length) {
    return null
  }

  const { title, category, productType, tags, collections } = getProductSearchValues(product)
  const fields = [
    { key: 'title', value: title },
    { key: 'category', value: category },
    { key: 'productType', value: productType },
    { key: 'collections', value: collections.join(' ') },
    { key: 'tags', value: tags.join(' ') },
  ]

  let bestScore = null

  fields.forEach(({ key, value }) => {
    const fieldPriority = SEARCH_FIELD_PRIORITIES[key] ?? 10

    variants.forEach((variant) => {
      const score = scoreSearchVariantAgainstField(value, variant, fieldPriority)

      if (score === null) {
        return
      }

      if (bestScore === null || score < bestScore) {
        bestScore = score
      }
    })
  })

  return bestScore
}

function getCanonicalSelection(value) {
  const normalizedSelection = normalizeSelectionKey(value)

  if (!normalizedSelection) {
    return ''
  }

  return COLLECTION_SELECTION_ALIASES[normalizedSelection] ?? normalizedSelection
}

export function matchesSearchQuery(product, searchQuery = '') {
  const searchTermGroups = getSearchTermGroups(searchQuery)

  if (!searchTermGroups.length) {
    return true
  }

  return searchTermGroups.some((searchTerm) => scoreProductSearchTerm(product, searchTerm) !== null)
}

export function getSearchRelevanceRank(product, searchQuery = '') {
  const searchTermGroups = getSearchTermGroups(searchQuery)

  if (!searchTermGroups.length) {
    return null
  }

  const termScores = searchTermGroups
    .map((searchTerm) => scoreProductSearchTerm(product, searchTerm))
    .filter((score) => score !== null)

  if (!termScores.length) {
    return null
  }

  const unmatchedTerms = searchTermGroups.length - termScores.length
  const bestScore = Math.min(...termScores)
  const averageScore = termScores.reduce((total, score) => total + score, 0) / termScores.length
  const stylePenalty = getProductSearchStylePenalty(product, searchQuery)

  return Math.round(
    unmatchedTerms * 1000 +
      bestScore * 10 +
      averageScore +
      stylePenalty,
  )
}

export function matchesCatalogSelection(product, selection) {
  const canonicalSelection = getCanonicalSelection(selection)

  if (
    !canonicalSelection ||
    canonicalSelection === 'all' ||
    canonicalSelection === 'all-vibes'
  ) {
    return true
  }

  const selectionRule = COLLECTION_SELECTION_RULES[canonicalSelection]

  if (!selectionRule) {
    return false
  }

  if (selectionRule.alwaysMatch) {
    return true
  }

  const productTagTokens = getProductTagTokens(product)

  if (!productTagTokens.length) {
    return false
  }

  const hasAllowedMatch = selectionRule.allowedTokens.some((expectedToken) =>
    productTagTokens.some(
      (candidateToken) => candidateToken === expectedToken,
    ),
  )

  if (hasAllowedMatch) {
    return true
  }

  return false
}

export function matchesStrictCollectionTags(product, collectionHandle) {
  return matchesCatalogSelection(product, collectionHandle)
}

export function getCollectionHandleForVibe(value) {
  return VIBE_COLLECTION_HANDLES[normalizeText(value)] ?? ''
}

export function getCollectionPathForVibe(value) {
  const collectionHandle = getCollectionHandleForVibe(value)

  return collectionHandle ? `/collections/${collectionHandle}` : '/collections/all'
}

export function getVibeForCollectionHandle(value) {
  const normalizedCollectionHandle = normalizeCollectionHandle(value)
  const entry = Object.entries(VIBE_COLLECTION_HANDLES).find(
    ([, collectionHandle]) => collectionHandle === normalizedCollectionHandle,
  )

  return entry?.[0] ?? 'all'
}

export function getCollectionDisplayName(value) {
  const normalizedCollectionHandle = normalizeCollectionHandle(value)

  if (COLLECTION_DISPLAY_NAMES[normalizedCollectionHandle]) {
    return COLLECTION_DISPLAY_NAMES[normalizedCollectionHandle]
  }

  if (!normalizedCollectionHandle) {
    return 'Collection'
  }

  return normalizedCollectionHandle
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}
